// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

/*
  Alarm notifications of a user: each notification sends one email per period, listing all
  alarms of the selected sources and sites. This replaces the former alarm emails of the site
  settings, which sent one email per alarm occurrence.
*/

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Col, Collapse, Form, Modal, Row } from "react-bootstrap";

import { AlarmNotification, NotificationPeriod } from "shared/types/cloudtypes";
import { AlarmSource } from "shared/types/globtypes";
import { isSet } from "shared/utils/utils";
import { setSelectedIds, tabClickHandler } from "guiutils";
import { DataState } from "types";
import {
  ActionType,
  ChangedAlarmNotification,
  IAction,
  IActionTypeChangeAlarmNotification,
  raiseAction,
} from "actions";
import { IState } from "states/states";
import { Site } from "states/sitestates";
import { UserSettingsArea } from "states/guistates";

import CheckList from "view/comp/CheckList";
import SaveFeedback from "view/comp/SaveFeedback";
import SiteSelectionTable from "view/comp/SiteSelectionTable";
import IconEdit from "icons/iconEdit";
import IconExpandMore from "icons/iconExpandMore";
import IconExpandLess from "icons/iconExpandLess";

const EMPTY_NOTIFICATIONS: AlarmNotification[] = [];
// number of sites or alarm types listed in the overview before only their count is shown
const MAX_LIST_ITEMS = 3;

interface IAlarmNotificationsExtProps {
  // the user owning the notifications, undefined for a not yet created user
  userId: number | undefined;
  userEmail: string | undefined;
}

interface IAlarmNotificationsProps {
  notificationList: AlarmNotification[];
  siteList: Site[];
  dataState: DataState;
  openedUserSettingsArea: UserSettingsArea[] | undefined;
  isExpanded: boolean;
  hasUserAdminRights: boolean;
}

const mapStateToProps = (state: IState, ownProps: IAlarmNotificationsExtProps) => {
  const loginUser = state.site.userList?.find(
    (curUser) => curUser.secId === state.site.user?.secId,
  );

  const props: IAlarmNotificationsProps = {
    // the loaded list belongs to the shown user, but may still be the one of a previous user
    notificationList: (state.site.alarmNotificationList ?? EMPTY_NOTIFICATIONS).filter(
      (curNotification) => curNotification.userId === ownProps.userId,
    ),
    siteList: state.site.siteList ?? [],
    dataState: state.scheduler.saveState,
    openedUserSettingsArea: state.gui.openedUserSettingsArea,
    isExpanded:
      state.gui.openedUserSettingsArea?.includes(UserSettingsArea.ALARM_NOTIFICATIONS) ?? false,
    hasUserAdminRights: loginUser?.accessRights?.modifyUsers ?? false,
  };
  return props;
};

interface IAlarmNotificationsState {
  // the notification currently edited, undefined if the dialog is closed
  editItem: ChangedAlarmNotification | undefined;
  // a store request was sent, so the dialog reports its progress and errors
  showSaveFeedback: boolean;
  // the store request is still pending
  isSaving: boolean;
}

interface IAlarmNotificationsDispatch {
  onEditClick: () => void;
  onSaveClick: (notification: ChangedAlarmNotification) => void;
  onDeleteClick: (notification: ChangedAlarmNotification) => void;
  onAreaToggle: (areas: UserSettingsArea[] | undefined, area: UserSettingsArea) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IAlarmNotificationsDispatch = {
    onEditClick: () => {
      // drop the save state of a previous notification change
      dispatch(raiseAction(ActionType.ACT_EDIT_ALARM_NOTIFICATION));
    },
    onSaveClick: (notification: ChangedAlarmNotification) => {
      const payload: IActionTypeChangeAlarmNotification = { notification };
      dispatch(raiseAction(ActionType.ACT_CHANGE_ALARM_NOTIFICATION_REQ, payload));
    },
    onDeleteClick: (notification: ChangedAlarmNotification) => {
      const payload: IActionTypeChangeAlarmNotification = { notification, isDelete: true };
      dispatch(raiseAction(ActionType.ACT_CHANGE_ALARM_NOTIFICATION_REQ, payload));
    },
    onAreaToggle: (areas: UserSettingsArea[] | undefined, area: UserSettingsArea) => {
      areas =
        !areas ? [area]
        : areas.includes(area) ? areas.filter((curArea) => curArea !== area)
        : [...areas, area];
      dispatch(raiseAction(ActionType.ACT_USER_SETTINGS_AREA_TOGGLE, areas));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  nameHint: {
    id: "custom name of notification",
  },
  recipientsHint: {
    id: "comma separated email addresses",
  },
  msgEdit: {
    id: "edit notification",
  },
  allSources: {
    id: "all alarm types",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  msgSiteCount: { count: number };
  msgSourceCount: { count: number };
}>({
  msgSiteCount: {
    id: "site count",
    defaultMessage: "{count} sites",
  },
  msgSourceCount: {
    id: "alarm type count",
    defaultMessage: "{count} alarm types",
  },
});

class AlarmNotificationsComponent extends React.Component<
  IAlarmNotificationsExtProps & IAlarmNotificationsProps & IAlarmNotificationsDispatch,
  IAlarmNotificationsState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IAlarmNotificationsState = {
    editItem: undefined,
    showSaveFeedback: false,
    isSaving: false,
  };

  // sites are only worth selecting if the user may access more than one
  private get hasSiteSelection() {
    return this.props.siteList.length > 1;
  }

  private makeEmptyNotification(): ChangedAlarmNotification {
    // without an id the api creates a new notification
    return {
      userId: this.props.userId,
      isActive: true,
      period: NotificationPeriod.HOUR,
      sources: Object.values(AlarmSource),
      includeCleared: true,
      allSites: true,
      siteIds: [],
    };
  }

  public componentDidUpdate() {
    if (this.state.isSaving) {
      // on success we are done, on error the dialog stays open to correct the data
      if (this.props.dataState === DataState.SAVE_SUCC) {
        this.setState({ isSaving: false, editItem: undefined });
      } else if (this.props.dataState === DataState.SAVE_FAIL) {
        this.setState({ isSaving: false });
      }
    }
  }

  public render() {
    return (
      <div id="alarmnotifications">
        <h1>
          <span
            className="link"
            onClick={() => {
              this.props.onAreaToggle(
                this.props.openedUserSettingsArea,
                UserSettingsArea.ALARM_NOTIFICATIONS,
              );
            }}
          >
            {this.props.hasUserAdminRights && (
              <Button variant="link" className="expand-button me-2 d-print-none">
                {this.props.isExpanded ?
                  <IconExpandMore />
                : <IconExpandLess />}
              </Button>
            )}
            <FormattedMessage id="Alarm Notifications" />
          </span>
        </h1>
        <Collapse in={this.props.isExpanded || !this.props.hasUserAdminRights}>
          <div>
            <p>
              <FormattedMessage id="For each notification one email per period is sent, listing all alarms of that period." />
            </p>
            {this.renderNotificationList()}
            <Button
              variant="outline-success"
              id="createNotification"
              className="mt-3 mb-3"
              onClick={() => {
                this.props.onEditClick();
                this.setState({ editItem: this.makeEmptyNotification(), showSaveFeedback: false });
              }}
            >
              <FormattedMessage id="Create New" />
            </Button>
            {this.renderEditModal()}
          </div>
        </Collapse>
      </div>
    );
  }

  private renderNotificationList() {
    if (!this.props.notificationList.length) {
      return (
        <p>
          <FormattedMessage id="No alarm notifications configured." />
        </p>
      );
    }

    const rows = this.props.notificationList.map((curNotification) => (
      <tr key={curNotification.id}>
        <td>
          <span className={curNotification.isActive ? "" : "deactivated"}>
            {curNotification.name}
          </span>
        </td>
        <td>
          <FormattedMessage id={`NotificationPeriod_${curNotification.period}`} />
        </td>
        <td>{this.getSiteText(curNotification)}</td>
        <td>{this.getSourceText(curNotification)}</td>
        <td>
          <span
            className="img-link"
            tabIndex={0}
            onKeyDown={tabClickHandler}
            onClick={() => {
              this.props.onEditClick();
              this.setState({ editItem: { ...curNotification }, showSaveFeedback: false });
            }}
            title={this.context.formatMessage(messages.msgEdit)}
          >
            <IconEdit />
          </span>
        </td>
      </tr>
    ));

    return (
      <table className="fullwidthwhite">
        <thead>
          <tr>
            <th>
              <FormattedMessage id="Title" />
            </th>
            <th>
              <FormattedMessage id="Period" />
            </th>
            <th>
              <FormattedMessage id="Sites" />
            </th>
            <th>
              <FormattedMessage id="Activated Alarms" />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    );
  }

  private getSiteText(notification: AlarmNotification) {
    if (notification.allSites) {
      return this.context.formatMessage({ id: "all sites" });
    }
    if (notification.siteIds.length > MAX_LIST_ITEMS) {
      return this.context.formatMessage(valueMessages.msgSiteCount, {
        count: notification.siteIds.length,
      });
    }
    return this.props.siteList
      .filter((curSite) => notification.siteIds.includes(curSite.id))
      .map((curSite) => curSite.name)
      .join(", ");
  }

  private getSourceText(notification: AlarmNotification) {
    if (notification.sources.length === Object.values(AlarmSource).length) {
      return this.context.formatMessage(messages.allSources);
    }
    if (notification.sources.length > MAX_LIST_ITEMS) {
      return this.context.formatMessage(valueMessages.msgSourceCount, {
        count: notification.sources.length,
      });
    }
    return notification.sources
      .map((curSource) => this.context.formatMessage({ id: `AlarmSource_${curSource}` }))
      .join(", ");
  }

  private changeSites = (siteIds: number[], isSelected: boolean) => {
    this.setState((state) => ({
      editItem: {
        ...state.editItem!,
        siteIds: setSelectedIds(state.editItem!.siteIds, siteIds, isSelected),
      },
    }));
  };

  private changeItem(change: Partial<ChangedAlarmNotification>) {
    this.setState({ editItem: { ...this.state.editItem!, ...change } });
  }

  private renderEditModal() {
    const item = this.state.editItem;
    if (!item) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={true} onHide={() => this.setState({ editItem: undefined })}>
        <Modal.Header closeButton>
          <FormattedMessage id="Alarm Notification" />
        </Modal.Header>
        <Modal.Body>
          {this.renderName(item)}
          {this.renderPeriod(item)}
          {this.renderRecipients(item)}
          {this.renderSources(item)}
          {this.hasSiteSelection && this.renderSites(item)}
          {this.hasSiteSelection && !item.allSites && (
            <SiteSelectionTable
              id="notificationSite"
              sites={this.props.siteList}
              selectedIds={item.siteIds}
              onChange={this.changeSites}
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          {this.state.showSaveFeedback && <SaveFeedback dataState={this.props.dataState} />}
          {isSet(item.id) && (
            <Button
              id="deleteNotification"
              variant="danger"
              className="me-auto"
              disabled={this.state.isSaving}
              onClick={() => {
                this.props.onDeleteClick(item);
                this.setState({ isSaving: true, showSaveFeedback: true });
              }}
            >
              <FormattedMessage id="Delete" />
            </Button>
          )}
          <Button
            id="cancelNotification"
            variant="secondary"
            className="me-1"
            onClick={() => this.setState({ editItem: undefined })}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            id="saveNotification"
            variant="primary"
            disabled={
              this.state.isSaving ||
              !item.sources.length ||
              (this.hasSiteSelection && !item.allSites && !item.siteIds.length)
            }
            onClick={() => {
              this.props.onSaveClick(
                this.hasSiteSelection ? item : { ...item, allSites: true, siteIds: [] },
              );
              this.setState({ isSaving: true, showSaveFeedback: true });
            }}
          >
            <FormattedMessage id="Save" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderName(item: ChangedAlarmNotification) {
    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column htmlFor="notificationName" sm={4}>
          <FormattedMessage id="Title" />
        </Form.Label>
        <Col sm={8}>
          <Form.Control
            id="notificationName"
            type="text"
            placeholder={this.context.formatMessage(messages.nameHint)}
            value={item.name ?? ""}
            onChange={(event) => this.changeItem({ name: event.currentTarget.value })}
          />
          <Form.Check
            id="notificationActive"
            className="mt-2"
            checked={item.isActive}
            onChange={(event) => this.changeItem({ isActive: event.currentTarget.checked })}
            label={<FormattedMessage id="Activated" />}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderPeriod(item: ChangedAlarmNotification) {
    const periodItems = Object.values(NotificationPeriod).map((curPeriod) => (
      <option key={curPeriod} value={curPeriod}>
        {this.context.formatMessage({ id: `NotificationPeriod_${curPeriod}` })}
      </option>
    ));

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column htmlFor="notificationPeriod" sm={4}>
          <FormattedMessage id="Notification Interval" />
        </Form.Label>
        <Col sm={8}>
          <Form.Select
            id="notificationPeriod"
            value={item.period}
            onChange={(event) =>
              this.changeItem({ period: event.currentTarget.value as NotificationPeriod })
            }
          >
            {periodItems}
          </Form.Select>
          <Form.Check
            id="notificationIncludeCleared"
            className="mt-2"
            checked={item.includeCleared}
            onChange={(event) => this.changeItem({ includeCleared: event.currentTarget.checked })}
            label={<FormattedMessage id="Also notify on cleared alarms" />}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderRecipients(item: ChangedAlarmNotification) {
    // without recipients the notification is sent to the email address of the user
    const hasRecipients = isSet(item.recipients);

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column className="pt-0" htmlFor="notificationRecipients" sm={4}>
          <FormattedMessage id="Recipient" />
        </Form.Label>
        <Col sm={8}>
          {this.props.hasUserAdminRights ?
            <>
              {!hasRecipients && <span>{this.props.userEmail}</span>}
              <Form.Check
                id="notificationOtherRecipients"
                checked={hasRecipients}
                onChange={(event) =>
                  this.changeItem({ recipients: event.currentTarget.checked ? "" : undefined })
                }
                label={<FormattedMessage id="Use other email address" />}
              />
              {hasRecipients && (
                <>
                  <Form.Control
                    id="notificationRecipients"
                    className="mt-2"
                    type="text"
                    placeholder={this.context.formatMessage(messages.recipientsHint)}
                    value={item.recipients}
                    onChange={(event) =>
                      this.changeItem({ recipients: event.currentTarget.value.replace(";", ",") })
                    }
                  />
                  <Form.Text className="text-danger">
                    <FormattedMessage id="Not recommended: other recipients cannot edit or acknowledge alarms." />
                  </Form.Text>
                </>
              )}
            </>
          : <span>{hasRecipients ? item.recipients : this.props.userEmail}</span>}
        </Col>
      </Form.Group>
    );
  }

  private renderSites(item: ChangedAlarmNotification) {
    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column className="pt-0" sm={4}>
          <FormattedMessage id="Sites" />
        </Form.Label>
        <Col sm={8}>
          <Form.Check
            id="notificationAllSites"
            checked={item.allSites}
            onChange={(event) => this.changeItem({ allSites: event.currentTarget.checked })}
            label={<FormattedMessage id="all sites" />}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderSources(item: ChangedAlarmNotification) {
    const sourceOptions = Object.values(AlarmSource).map((curSource) => ({
      value: curSource,
      label: this.context.formatMessage({ id: `AlarmSource_${curSource}` }),
    }));

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column className="pt-0" sm={4}>
          <FormattedMessage id="Activated Alarms" />
        </Form.Label>
        <Col sm={8}>
          <CheckList
            id="notificationSources"
            options={sourceOptions}
            selected={item.sources}
            onChange={(sources) => this.changeItem({ sources })}
          />
        </Col>
      </Form.Group>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AlarmNotificationsComponent);
